import React from 'react';
import styled, { css } from 'styled-components';

import { BRAND_COLORS } from './colors';
import { createLoadingSpinner } from './Loading-styled-components';
import { TYPOGRAPHY } from './typography';

interface BaseButtonProps {
  $loading?: boolean;
  $disabled?: boolean;
  type?: string;
}

const disabledStyles = css`
  /* 60% opacity of darkGray */
  /* See https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/rgb relative value syntax */
  color: rgb(from ${BRAND_COLORS.darkGray} r g b / 0.6);
  background-color: ${BRAND_COLORS.lightGray};
  border-color: ${BRAND_COLORS.lightGray};
  cursor: not-allowed;
`;

// If disabled is passed as a prop, allow clicks by not using the HTML disabled attribute
const BaseButton = styled.button.attrs<BaseButtonProps>(({ $loading, type }) => ({
  type: type || 'button',
  disabled: $loading,
}))`
  ${TYPOGRAPHY.CIRCULAR.bold}
  font-size: 16px;
  line-height: 20px;
  text-align: center;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;

  border-radius: 64px;
  border: 1px solid;

  transition-property: background-color, color, border-color;
  transition-duration: 200ms;
  transition-timing-function: ease-in-out;

  cursor: pointer;

  &:hover {
    color: ${BRAND_COLORS.white};
  }

  /* Show disabled styles when pseudo-disabled by prop and when disabled by HTML */
  &:disabled,
  &:disabled:hover {
    pointer-events: none;
    ${disabledStyles}
  }

  ${({ $disabled }) => $disabled && css`
    && {
      ${disabledStyles}

      &:hover {
        ${disabledStyles}
      }
    }
  `}
  ${({ $loading }) => $loading && css`
    cursor: progress;
    &::after {
      content: '';
      display: inline-block;
      /* Size is line-height - 2 * border-width */
      ${createLoadingSpinner({ size: 16, color: BRAND_COLORS.white })}
    }
  `}
`;

export const PrimaryButton = styled(BaseButton)`
  border-color: ${BRAND_COLORS.darkGreen};
  background: ${BRAND_COLORS.darkGreen};
  color: ${BRAND_COLORS.white};

  &:hover {
    background-color: ${BRAND_COLORS.lightGreen};
    border-color: ${BRAND_COLORS.lightGreen};
  }
`;

export const TertiaryButton = styled(BaseButton)`
  border-color: ${BRAND_COLORS.darkGreen};
  background: ${BRAND_COLORS.white};
  color: ${BRAND_COLORS.darkGreen};

  &:hover {
    background-color: ${BRAND_COLORS.darkGreen};
  }
`;

const LinkButtonText = styled.span`
  width: fit-content;
`;

interface StyledLinkButtonProps {
  $isUnderlined?: boolean;
  $isBold?: boolean;
}
const StyledLinkButton = styled.button.attrs({
  type: 'button',
})<StyledLinkButtonProps>`
  /* Makes the button act as regular text */
  display: contents;

  color: ${BRAND_COLORS.darkGreen};
  cursor: pointer;

  ${({ $isBold }) => $isBold ? TYPOGRAPHY.OPEN_SANS.semiBold : TYPOGRAPHY.OPEN_SANS.base}

  &:not(:disabled) {
    &:hover {
      ${LinkButtonText} {
        color: ${BRAND_COLORS.lightGreen};
        text-decoration: underline;
      }
    }
    ${({ $isUnderlined }) => $isUnderlined && css`
      ${LinkButtonText} {
        text-decoration: underline;
      }
    `}
  }

  &:disabled {
    color: ${BRAND_COLORS.darkGray};
    text-decoration: none;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

interface LinkButtonProps {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

// Children are wrapped in a span since `display: contents` prevents underlining from working
export const LinkButton = ({ children, ...props }: LinkButtonProps & StyledLinkButtonProps) => (
  <StyledLinkButton {...props}>
    <LinkButtonText>
      {children}
    </LinkButtonText>
  </StyledLinkButton>
);
